import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { BuildingThreeIcon, ChevronLeftIcon, ProviderIcon } from '@repo/ui/icons'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import { getCenterCurrency } from '../../../api/centers/get-center-currency'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import withCenter, { WithCenterProps } from '../../../components/hoc/with-center'
import { CenterEventTable } from '../../../components/pages/center-events/center-event-table'
import OlCenterCapsuleTab from '../../../components/pages/ol-common/ol-center-capsule-tab'
import OlPersonalSpaceCenterGroups from '../../../components/pages/ol-personal-space/ol-personal-space-center-groups'
import OlPersonalSpaceCenterPlacement from '../../../components/pages/ol-personal-space/ol-personal-space-center-placement'
import OlPersonalSpaceCenterProgramAndActivities from '../../../components/pages/ol-personal-space/ol-personal-space-center-program/ol-personal-space-center-program-and-activities'
import OlPersonalSpaceCenterTripsInfo from '../../../components/pages/ol-personal-space/ol-personal-space-center-trips/ol-personal-space-center-trips-info'
import OlCenterJourneyBadgeDetails from '../../../components/pages/ols/centers/ol-center-journey-badge-details'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import CurrencyProvider from '../../../lib/contexts/currency'
import { TimezoneProvider } from '../../../lib/contexts/timezone'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'
import NotFoundPage from '../../common/not-found'
import { CostOfCenterPage } from './cost-of-center'
import { FinalCostOfCenterPage } from './final-cost-of-center'

const OlPersonalSpaceCenterDetails = ({ center, refetch }: WithCenterProps) => {
  const { id } = useParams<{ id: string }>()
  const { navigate } = useNavigator()
  const { user } = useAuth()
  const { t } = useTranslate()

  const { data: centerCurrency } = useQuery({
    queryKey: ['center-currency', center?.id],
    queryFn: () => {
      if (center) return getCenterCurrency(center?.id)
    },
    enabled: !!center?.id
  })

  const journeyPlace = center?.journeyPlace
  const timezone = journeyPlace?.timezone ?? journeyPlace?.country.timezone
  const startDate = center?.startDate ? DateFormatter.DDMMYYYY(center.startDate) : ''
  const endDate = center?.endDate ? DateFormatter.DDMMYYYY(center.endDate) : ''

  const centerNameWithDates = `${center?.name}${startDate && endDate ? ` - ${startDate} ${t('common.to')} ${endDate}` : ''}`

  const contactInParisEmail = center?.ol?.contactInParis?.email
  if (!center || !id || !centerCurrency) return <LoadingScreen />

  return (
    <CurrencyProvider currency={centerCurrency.currency}>
      <div>
        <UserNavBar icon={BuildingThreeIcon} title={centerNameWithDates} />
        <div className={'h-4'} />
        <Box>
          <div className="flex justify-between items-center">
            <Button
              icon={ChevronLeftIcon}
              label={t('ol.center.back.button')}
              variant="secondary"
              onClick={() => navigate(Paths.OL_PERSONAL_SPACE_CENTER_LIST_PAGE)}
            />
            {center.ol && (
              <Badge
                variant="white"
                size="flexible"
                className="rounded"
                iconPosition="start"
                icon={<Badge icon={<ProviderIcon color={tailwindColors.grey.lighter} />} />}
                text={
                  <div className="flex gap-2 items-center">
                    <Typography variant="label" color={tailwindColors.grey.lighter}>
                      {t('ol.center.prestataire')}:
                    </Typography>
                    {contactInParisEmail ? (
                      <TextLink
                        href={`mailto:${contactInParisEmail}`}
                        name={contactInParisEmail}
                      />
                    ) : (
                      <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                        -
                      </Typography>
                    )}

                    <Typography variant="label" color={tailwindColors.grey.lighter}>
                      {t('ol.center.lec.phone.number')}:
                    </Typography>
                    <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                      +33 1 42 67 55 55
                    </Typography>
                  </div>
                }
              />
            )}
          </div>

          {/** Tabs for the center details */}

          <div className="h-4" />
          <OlCenterJourneyBadgeDetails center={center} />
          <div className="h-4" />

          {/** Tabs for the center details */}
          <OlCenterCapsuleTab centerId={+id} />

          {/**Tab View  */}
          <Routes>
            <Route
              path={Paths.OL_CENTER_PLACEMENTS}
              element={<OlPersonalSpaceCenterPlacement id={Number.parseInt(id)} />}
            />
            <Route
              path={Paths.OL_CENTER_GROUPS}
              element={<OlPersonalSpaceCenterGroups id={Number.parseInt(id)} />}
            />
            <Route
              path={Paths.OL_CENTER_COST}
              element={<CostOfCenterPage data={center} refetch={refetch} />}
            />
            <Route
              path={Paths.OL_CENTER_ACTIVITIES_TEFL_AND_TEACHING_HALLS}
              element={
                <TimezoneProvider defaultTimezone={timezone}>
                  <OlPersonalSpaceCenterProgramAndActivities
                    id={Number.parseInt(id)}
                    center={center}
                  />
                </TimezoneProvider>
              }
            />
            <Route
              path={Paths.OL_CENTER_TRIP_INFORMATION}
              element={
                <OlPersonalSpaceCenterTripsInfo
                  id={Number.parseInt(id)}
                  tripBookingReleased={Boolean(center.tripBookingDiffusedAt)}
                />
              }
            />
            <Route
              path={Paths.OL_CENTER_FINAL_COST}
              element={<FinalCostOfCenterPage data={center} refetch={refetch} />}
            />
            <Route
              path={Paths.OL_CENTER_EVENTS}
              element={
                getOlPermissions({ role: user.role, type: center.ol?.type })['center-events'] && (
                  <div className="mt-5">
                    <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
                      {t('center.events.title')}
                    </Typography>
                    <div className="h-3" />
                    <CenterEventTable centerId={center.id} version={'OL_VIEW'} headerType="none" />
                  </div>
                )
              }
            />
            <Route index element={<Navigate to={Paths.OL_CENTER_PLACEMENTS} />} />
            <Route path={'*'} element={<NotFoundPage />} />
          </Routes>
        </Box>
      </div>
    </CurrencyProvider>
  )
}
export default withCenter(OlPersonalSpaceCenterDetails)
